/**
 * Per-user default formatting for new compose / reply / forward.
 * Mirrors backend `email-compose-defaults.util.ts`.
 */

export type EmailComposeParagraphStyle =
  | 'body'
  | 'p'
  | 'h1'
  | 'h2'
  | 'h3'
  | 'h4'
  | 'h5'
  | 'h6'
  | 'address'
  | 'pre';

export interface EmailComposeDefaults {
  paragraphStyle: EmailComposeParagraphStyle;
  /** Empty string = Variable Width (browser/default). */
  fontFamily: string;
  /** CSS font-size with px, e.g. "14px". */
  fontSize: string;
  /** CSS color, preferably #rrggbb. */
  fontColor: string;
  /** Include sent/MRU history in To/Cc/Bcc autocomplete. */
  includeSentHistoryEmails: boolean;
  /** Include address book contacts in To/Cc/Bcc autocomplete. */
  includeAddressBookEmails: boolean;
}

export const EMAIL_COMPOSE_PARAGRAPH_STYLE_OPTIONS: Array<{
  value: EmailComposeParagraphStyle;
  label: string;
}> = [
  { value: 'body', label: 'Body Text' },
  { value: 'p', label: 'Paragraph' },
  { value: 'h1', label: 'Heading 1' },
  { value: 'h2', label: 'Heading 2' },
  { value: 'h3', label: 'Heading 3' },
  { value: 'h4', label: 'Heading 4' },
  { value: 'h5', label: 'Heading 5' },
  { value: 'h6', label: 'Heading 6' },
  { value: 'address', label: 'Address' },
  { value: 'pre', label: 'Preformat' },
];

export const EMAIL_COMPOSE_FONT_SIZES_PX = [10, 12, 14, 16, 18, 20, 24, 28, 32] as const;

const PARAGRAPH_STYLE_TAG: Record<EmailComposeParagraphStyle, string> = {
  body: 'div',
  p: 'p',
  h1: 'h1',
  h2: 'h2',
  h3: 'h3',
  h4: 'h4',
  h5: 'h5',
  h6: 'h6',
  address: 'address',
  pre: 'pre',
};

export function defaultEmailComposeDefaults(): EmailComposeDefaults {
  return {
    paragraphStyle: 'p',
    fontFamily: '',
    fontSize: '14px',
    fontColor: '#000000',
    includeSentHistoryEmails: true,
    includeAddressBookEmails: true,
  };
}

export function sanitizeEmailComposeDefaults(input: unknown): EmailComposeDefaults {
  const defaults = defaultEmailComposeDefaults();
  if (!input || typeof input !== 'object') return defaults;
  const o = input as Record<string, unknown>;
  const style = o.paragraphStyle;
  const paragraphStyle = EMAIL_COMPOSE_PARAGRAPH_STYLE_OPTIONS.some((opt) => opt.value === style)
    ? (style as EmailComposeParagraphStyle)
    : defaults.paragraphStyle;

  const fontFamily = typeof o.fontFamily === 'string' ? o.fontFamily.trim().slice(0, 200) : defaults.fontFamily;

  let fontSize = defaults.fontSize;
  if (typeof o.fontSize === 'string') {
    const match = o.fontSize.trim().match(/^(\d{1,2})(?:px)?$/i);
    if (match) {
      const px = Number.parseInt(match[1], 10);
      if ((EMAIL_COMPOSE_FONT_SIZES_PX as readonly number[]).includes(px)) {
        fontSize = `${px}px`;
      }
    }
  }

  let fontColor = defaults.fontColor;
  if (typeof o.fontColor === 'string') {
    const raw = o.fontColor.trim();
    if (/^#[0-9a-f]{6}$/i.test(raw)) fontColor = raw.toLowerCase();
    else if (/^#[0-9a-f]{3}$/i.test(raw)) {
      fontColor = `#${raw[1]}${raw[1]}${raw[2]}${raw[2]}${raw[3]}${raw[3]}`.toLowerCase();
    }
  }

  const includeSentHistoryEmails =
    typeof o.includeSentHistoryEmails === 'boolean'
      ? o.includeSentHistoryEmails
      : defaults.includeSentHistoryEmails;
  const includeAddressBookEmails =
    typeof o.includeAddressBookEmails === 'boolean'
      ? o.includeAddressBookEmails
      : defaults.includeAddressBookEmails;

  return {
    paragraphStyle,
    fontFamily,
    fontSize,
    fontColor,
    includeSentHistoryEmails,
    includeAddressBookEmails,
  };
}

function escapeHtmlAttr(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/** Build styled empty user-body HTML so Slate starts with the user's compose defaults. */
export function buildDefaultComposeUserBodyHtml(
  defaults: EmailComposeDefaults = defaultEmailComposeDefaults(),
): string {
  const styleParts: string[] = [];
  if (defaults.fontFamily.trim()) {
    // Avoid raw " inside style="..." (toolbar stacks like Courier, "Courier New", monospace).
    styleParts.push(`font-family:${defaults.fontFamily.trim().replace(/"/g, "'")}`);
  }
  const isHeading = /^h[1-6]$/.test(defaults.paragraphStyle);
  if (defaults.fontSize.trim() && !isHeading) {
    styleParts.push(`font-size:${defaults.fontSize.trim()}`);
  }
  if (defaults.fontColor.trim()) {
    styleParts.push(`color:${defaults.fontColor.trim()}`);
  }
  const inner =
    styleParts.length > 0
      ? `<span style="${escapeHtmlAttr(styleParts.join(';'))}"><br></span>`
      : '<br>';
  const tag = PARAGRAPH_STYLE_TAG[defaults.paragraphStyle] ?? 'p';
  return `<${tag}>${inner}</${tag}>`;
}

const EMPTY_BODY_PATTERNS = [
  '',
  '<p><br></p>',
  '<p><br/></p>',
  '<p><br /></p>',
  '<p></p>',
  '<div><br></div>',
  '<div><br/></div>',
  '<div><br /></div>',
  '<div></div>',
];

/** True when the user body is effectively empty (placeholder only). */
export function isEmptyComposeUserBody(html: string | null | undefined): boolean {
  if (html == null) return true;
  const trimmed = html.trim();
  if (!trimmed) return true;
  if (EMPTY_BODY_PATTERNS.includes(trimmed)) return true;
  // Styled empty body from our builder (or similar)
  const withoutBr = trimmed
    .replace(/<br\s*\/?>/gi, '')
    .replace(/&nbsp;/gi, '')
    .replace(/\s+/g, '');
  const textOnly = withoutBr.replace(/<[^>]+>/g, '').trim();
  return textOnly.length === 0 && /<(p|div|h[1-6]|address|pre)\b/i.test(trimmed);
}

/**
 * If `html` is an empty placeholder, replace with the user's compose defaults.
 * Non-empty bodies (drafts, AI text, custom intro) are left unchanged.
 */
export function applyComposeDefaultsToUserBody(
  html: string | null | undefined,
  defaults: EmailComposeDefaults,
): string {
  if (!isEmptyComposeUserBody(html)) return html ?? '';
  return buildDefaultComposeUserBodyHtml(defaults);
}
